import React from 'react'
import { SpecialPeriod } from '../../api/special-periods'
import { useCreateMutation, useUpdateMutation } from '../../queries/special-periods'
import { Field, Form, Formik } from 'formik'
import InputField from '../form/fields/input'
import { RequestButton, RequestMessage } from '../request-components/request-components'
import AutocompleteField from '../form/fields/autocomplete'
import { Columns } from 'react-bulma-components'
import { dateInputToDateValue, dateToDateInputValue, formatISODate } from '../../utils/date'
import { AutocompleteFieldItem } from '../form/fields/autocomplete'

export interface SpecialPeriodFormProps {
  specialPeriod?: SpecialPeriod
  employerId: string // Required employer ID from context
  existingTypes?: string[] // Optional list of existing custom types for autocomplete
  onSuccess?: () => void
}

/**
 * Form component for creating and editing special periods
 * employerId is required and comes from the context (no employer selection field)
 * existingTypes provides suggestions for custom type values
 */
const SpecialPeriodForm: React.FC<SpecialPeriodFormProps> = ({
  specialPeriod,
  employerId,
  existingTypes = [],
  onSuccess,
}) => {
  const createMutation = useCreateMutation()
  const updateMutation = useUpdateMutation()

  const handleSuccess = () => {
    if (onSuccess) {
      onSuccess()
    }
  }

  // Convert date from API (ISO string or Date) to HTML date input format (YYYY-MM-DD)
  const getInitialDate = (date?: Date | string): string => {
    if (!date) return ''
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return dateToDateInputValue(dateObj)
  }

  // Convert HTML date input value to ISO 8601 string for API
  const dateInputToISO = (dateInput: string): string => {
    if (!dateInput) return ''
    const date = dateInputToDateValue(dateInput)
    return formatISODate(date)
  }

  // Convert existing types to autocomplete items, including "Jour férié"
  const typeSuggestions: AutocompleteFieldItem[] = React.useMemo(() => {
    const allTypes = new Set<string>()

    // Add all existing types
    existingTypes.forEach(type => {
      if (type) {
        allTypes.add(type)
      }
    })

    return Array.from(allTypes).map(type => ({
      value: type,
      label: type,
    }))
  }, [existingTypes])

  return (
    <Formik
      initialValues={{
        type: specialPeriod?.type || '',
        name: specialPeriod?.name || '',
        startDate: getInitialDate(specialPeriod?.startDate),
        endDate: getInitialDate(specialPeriod?.endDate),
      }}
      enableReinitialize
      validate={values => {
        const errors: any = {}

        // Type validation
        if (!values.type) {
          errors.type = 'Le type est requis'
        } else if (values.type.length < 1 || values.type.length > 255) {
          errors.type = 'Le type doit contenir entre 1 et 255 caractères'
        }

        // Name validation
        if (!values.name) {
          errors.name = 'Le nom est requis'
        } else if (values.name.length < 1 || values.name.length > 255) {
          errors.name = 'Le nom doit contenir entre 1 et 255 caractères'
        }

        // Start date validation
        if (!values.startDate) {
          errors.startDate = 'La date de début est requise'
        }

        // End date validation
        if (!values.endDate) {
          errors.endDate = 'La date de fin est requise'
        }

        // Date range validation
        if (values.startDate && values.endDate) {
          const startDate = dateInputToDateValue(values.startDate)
          const endDate = dateInputToDateValue(values.endDate)
          if (startDate > endDate) {
            errors.endDate = 'La date de début doit être antérieure ou égale à la date de fin'
          }
        }

        return errors
      }}
      onSubmit={values => {
        const payload = {
          employer: employerId,
          type: values.type,
          name: values.name,
          startDate: dateInputToISO(values.startDate),
          endDate: dateInputToISO(values.endDate),
        }

        if (specialPeriod) {
          updateMutation.mutate(
            {
              id: specialPeriod._id,
              ...payload,
            },
            { onSuccess: handleSuccess },
          )
        } else {
          createMutation.mutate(payload as any, { onSuccess: handleSuccess })
        }
      }}
    >
      {() => {
        return (
          <Form autoComplete="off">
            <Field
              label="Nom"
              name="name"
              component={InputField}
              placeholder="Ex: Noël, Fermeture août, Jour de l'an..."
              required
            />

            <Field
              label="Type"
              name="type"
              component={AutocompleteField}
              items={typeSuggestions}
              placeholder="Ex: Jour férié, Fermeture estivale, Journée spéciale..."
              required
              type="text"
            />
            <Columns>
              <Columns.Column size={6}>
                <Field
                  label="Date de début"
                  name="startDate"
                  component={InputField}
                  type="date"
                  required
                />
              </Columns.Column>
              <Columns.Column size={6}>
                <Field
                  label="Date de fin"
                  name="endDate"
                  component={InputField}
                  type="date"
                  required
                />
              </Columns.Column>
            </Columns>

            <RequestButton
              color="primary"
              type="submit"
              mutation={specialPeriod ? updateMutation : createMutation}
              data-test="save-special-period"
            >
              {specialPeriod ? 'Mettre à jour la période spéciale' : 'Créer une période spéciale'}
            </RequestButton>
            <RequestMessage mutation={specialPeriod ? updateMutation : createMutation} />
          </Form>
        )
      }}
    </Formik>
  )
}

export default SpecialPeriodForm
